/**
 * Analytics metrics available for experiment analysis
 */
export type AnalyticsMetric = 'registrations' | 'start_trials' | 'subscription_started' | 'listens' | 'start_sessions';

/**
 * Display labels for metrics
 */
export const METRIC_LABELS: Record<AnalyticsMetric, string> = {
  registrations: 'Registrations',
  start_trials: 'Trial Started',
  subscription_started: 'Subscription',
  listens: 'Listens',
  start_sessions: 'Sessions',
};

/**
 * Funnel dependencies - child metric must come from parent
 * null means this is a top-level metric with no dependency
 */
export const METRIC_DEPENDENCIES: Record<AnalyticsMetric, AnalyticsMetric | null> = {
  registrations: null, // Top of funnel
  start_trials: 'registrations', // Must come from registrations
  subscription_started: 'start_trials', // Must come from trials
  listens: 'registrations', // Independent branch from registrations
  start_sessions: 'registrations', // Independent branch from registrations
};

/**
 * All available metrics as an array
 */
export const ALL_METRICS: AnalyticsMetric[] = [
  'registrations',
  'start_trials',
  'subscription_started',
  'listens',
  'start_sessions',
];

/**
 * Retention cohort starting points
 */
export type RetentionFromCohort = 'registered' | 'trialists' | 'subscribers';

/**
 * Retention activity types to measure
 */
export type RetentionToActivity = 'sessions' | 'listens';

/**
 * Display labels for retention cohorts
 */
export const RETENTION_COHORT_LABELS: Record<RetentionFromCohort, string> = {
  registered: 'Registered Users',
  trialists: 'Trialists',
  subscribers: 'Subscribers',
};

/**
 * Display labels for retention activities
 */
export const RETENTION_ACTIVITY_LABELS: Record<RetentionToActivity, string> = {
  sessions: 'Sessions',
  listens: 'Listens',
};

/**
 * Funnel step data
 */
export type FunnelStep = {
  metric: AnalyticsMetric;
  label: string;
  count: number;
  percentage: number;
};

/**
 * Variation funnel data
 */
export type VariationFunnelData = {
  variationId: string;
  variationName: string;
  isControl: boolean;
  steps: FunnelStep[];
  totalConversionRate: number;
};

/**
 * Configurable funnel response
 */
export type ConfigurableFunnelData = {
  experimentId: string;
  experimentName: string;
  instanceName: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  funnelSteps: AnalyticsMetric[];
  variations: VariationFunnelData[];
};

/**
 * Single metric data per variation
 */
export type MetricData = {
  metric: AnalyticsMetric;
  label: string;
  count: number;
  rate?: number;
};

/**
 * Variation metrics data
 */
export type VariationMetricsData = {
  variationId: string;
  variationName: string;
  isControl: boolean;
  metrics: MetricData[];
};

/**
 * Individual metrics response
 */
export type IndividualMetricsData = {
  experimentId: string;
  experimentName: string;
  instanceName: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  requestedMetrics: AnalyticsMetric[];
  variations: VariationMetricsData[];
};

/**
 * Single day retention data
 */
export type RetentionDayData = {
  day: number;
  count: number;
  rate: number;
};

/**
 * Retention row for a cohort date
 */
export type RetentionRow = {
  cohortDate: string;
  cohortSize: number;
  retention: RetentionDayData[];
};

/**
 * Variation retention data
 */
export type VariationRetentionData = {
  variationId: string;
  variationName: string;
  isControl: boolean;
  cohorts: RetentionRow[];
};

/**
 * Retention response
 */
export type RetentionData = {
  experimentId: string;
  experimentName: string;
  instanceName: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  fromCohort: RetentionFromCohort;
  toActivity: RetentionToActivity;
  maxDays: number;
  variations: VariationRetentionData[];
};

/**
 * Experiment user data
 */
export type ExperimentUser = {
  userId: string;
  variationId: string;
  variationName: string;
  joinedAt: string;
  amplitudeUrl: string;
};

/**
 * Users response
 */
export type UsersData = {
  experimentId: string;
  experimentName: string;
  instanceName: string;
  users: ExperimentUser[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  amplitudeCohortUrl: string;
};

/**
 * Validate funnel steps respect metric dependencies
 * @param steps - Array of funnel steps to validate
 * @returns Array of error messages (empty if valid)
 */
export function validateFunnelSteps(steps: AnalyticsMetric[]): string[] {
  const errors: string[] = [];

  if (steps.length === 0) {
    errors.push('At least one funnel step is required');
    return errors;
  }

  for (let i = 1; i < steps.length; i++) {
    const currentStep = steps[i];
    const dependency = METRIC_DEPENDENCIES[currentStep];

    if (dependency !== null) {
      const dependencyIndex = steps.indexOf(dependency);
      if (dependencyIndex === -1 || dependencyIndex >= i) {
        errors.push(`${METRIC_LABELS[currentStep]} requires ${METRIC_LABELS[dependency]} to be included before it`);
      }
    }
  }

  return errors;
}
